import { useEffect } from "react";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "@/lib/firebase";
import { useSetFcmToken } from "@/hooks/useNotifications";
import { playNotificationSound } from "@/lib/notificationSound";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

export const NotificationInitializer = () => {
  const { mutate: setFcmToken } = useSetFcmToken();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const setupNotifications = async () => {
      if (!messaging) return;

      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          // Manually register service worker to avoid registration timeout errors
          const registration = await navigator.serviceWorker.register(
            "/firebase-messaging-sw.js",
            {
              scope: "/firebase-cloud-messaging-push-scope",
            },
          );

          const token = await getToken(messaging, {
            serviceWorkerRegistration: registration,
          });

          if (token) {
            setFcmToken({ device_token: token });
          }
        }
      } catch (error) {
        console.error("Error setting up notifications:", error);
      }
    };

    setupNotifications();

    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type === "PLAY_NOTIFICATION_SOUND") {
        playNotificationSound();
        // Refresh notification list so the red dot appears immediately
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
    };
    navigator.serviceWorker?.addEventListener("message", onSwMessage);

    // Foreground message handler
    if (messaging) {
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log("Foreground message received:", payload);
        playNotificationSound();
        toast.info(payload.notification?.title || "New Notification", {
          description: payload.notification?.body,
          action: payload.data?.orderId ? {
            label: "View",
            onClick: () => navigate(`/order-management/${payload.data!.orderId}`)
          } : undefined,
        });
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      });
      return () => {
        unsubscribe();
        navigator.serviceWorker?.removeEventListener("message", onSwMessage);
      };
    }

    return () => {
      navigator.serviceWorker?.removeEventListener("message", onSwMessage);
    };
  }, [setFcmToken, queryClient]);

  return null;
};
